<template>
    <RegularModal ref="modal" :modal-card-class="'is-large'">
        <template v-slot:title>
            <span class="icon">
                <i class="fa fa-user" />
            </span>
            <span>
                {{ translate("record_profile.modal_title") }}
            </span>
        </template>
        <template v-slot:body>
            <div v-if="initDone">
                <template v-if="requireLogin || requireVerifiedRegistrations">
                    <div class="columns">
                        <div class="column is-8 is-offset-2">
                            <div class="notification is-danger is-light has-text-centered">
                                <span class="icon">
                                    <i class="fa fa-exclamation-triangle" />
                                </span>
                                <span v-if="requireLogin">
                                    {{ translate("record_profile.login_required") }}
                                </span>
                                <span v-if="requireVerifiedRegistrations">
                                    {{ translate("record_profile.valid_registration_required") }}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div class="columns">
                        <div class="column has-text-centered">
                            <a class="button is-light is-medium" :href="urls.userPagesUrls().loginPage(currentPageBackUrl())">
                                <span class="icon">
                                    <i class="fa fa-sign-in-alt" />
                                </span>
                                <span>
                                    {{ translate("record_profile.go_to_login") }}
                                </span>
                            </a>
                        </div>
                    </div>
                </template>
                <template v-else>
                    <div class="profile-header mb-4">
                        <div class="profile-identity">
                            <CompetitorAvatar
                                :first-name="competitorRecordProfile.firstName"
                                :last-name="competitorRecordProfile.lastName"
                                :photo-url="competitorRecordProfile.photoUrl"
                                :size="88"
                            />
                            <div class="profile-details">
                                <p class="title is-4 mb-1">
                                    <Country
                                        v-if="competitorRecordProfile.nationality"
                                        :country-code="competitorRecordProfile.nationality"
                                    />
                                    <span class="clarity-mask">
                                        {{ competitorRecordProfile.firstName }} {{ competitorRecordProfile.lastName }}
                                    </span>
                                    <small class="competitor-id"> #{{ competitorRecordProfile.competitorId }} </small>
                                </p>
                                <p class="subtitle mb-0 profile-academy">
                                    <a :href="urls.publicPages().academyPage(competitorRecordProfile.academyId)">
                                        {{ competitorRecordProfile.academyName }}
                                    </a>
                                    / {{ competitorRecordProfile.branch }}
                                </p>
                            </div>
                        </div>
                        <div class="profile-stats">
                            <div class="is-size-2">
                                <span class="has-text-success">
                                    {{ competitorRecordProfile.overallFightRecord.wins }}
                                </span>
                                -
                                <span class="has-text-danger">
                                    {{ competitorRecordProfile.overallFightRecord.losses }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="columns has-text-centered" style="display: none">
                        <div class="column is-4 gold">
                            <span class="icon is-large">
                                <i class="fa fa-2x fa-medal" />
                            </span>
                            <span class="is-size-3">0</span>
                        </div>
                        <div class="column is-4 silver">
                            <span class="icon is-large">
                                <i class="fa fa-2x fa-medal" />
                            </span>
                            <span class="is-size-3"> 0 </span>
                        </div>
                        <div class="column is-4 bronze">
                            <span class="icon is-large">
                                <i class="fa fa-2x fa-medal" />
                            </span>
                            <span class="is-size-3">0</span>
                        </div>
                    </div>

                    <div class="columns">
                        <div class="column is-12">
                            <div class="tabs is-boxed">
                                <ul>
                                    <li :class="{ 'is-active': activeTab === 'history' }">
                                        <a @click="activeTab = 'history'">
                                            {{ translate("record_profile.history") }}
                                        </a>
                                    </li>
                                    <li :class="{ 'is-active': activeTab === 'statistics' }">
                                        <a @click="activeTab = 'statistics'">
                                            {{ translate("record_profile.statistics") }}
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            <StatisticsTab v-if="activeTab === 'statistics'" :statistics="statistics" :loading="loadingStatistics" />

                            <FightsHistoryTab
                                v-else
                                :fight-records="competitorRecordProfile.fightRecords"
                                :can-load-more="probablyMoreFightsToLoad"
                                :loading-more="loadingMoreFights"
                                @select-opponent="reloadCompetitor"
                                @load-more="loadMoreFights"
                            />
                        </div>
                    </div>
                </template>
            </div>
            <div class="columns is-mobile is-centered" v-else>
                <LoadingSpinner2 class="has-added-margin" />
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="goBack()" :disabled="!canGoBack">
                    <span class="icon">
                        <i class="fa fa-backward" />
                    </span>
                    <span>
                        {{ translate("common.go_back") }}
                    </span>
                </button>
                <button class="button" @click="close">
                    {{ translate("common.close") }}
                </button>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed, watch } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import StatisticsTab from "./StatisticsTab.vue";
    import FightsHistoryTab from "./FightsHistoryTab.vue";
    import CompetitorAvatar from "../Common/Competitors/CompetitorAvatar.vue";
    import Country from "./Country.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { useModal } from "../Common/useModal";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { currentPageBackUrl } from "../Common/currentPageBackUrl";
    import urls from "../Urls/urls";
    import recordProfileApi, {
        CompetitorRecordProfile,
        CompetitorOwnRecordProfile,
        FullFightRecord,
        RecordProfileStatistics,
    } from "../Api/recordProfile";

    interface CompetitorIdContext {
        userId: number;
        competitorId: number;
    }

    type HistoryEntry = PublicHistoryEntry | OwnHistoryEntry;

    interface PublicHistoryEntry {
        type: "public";
        eventId: number;
        publicId: string;
    }

    interface OwnHistoryEntry extends CompetitorIdContext {
        type: "own";
    }

    function isPublicProfile(profile: CompetitorRecordProfile | CompetitorOwnRecordProfile): profile is CompetitorRecordProfile {
        return "id" in profile;
    }

    const { modal, close } = useModal();

    const initDone = ref(false);
    const requireLogin = ref(false);
    const requireVerifiedRegistrations = ref(false);
    const historyStack = ref<HistoryEntry[]>([]);
    const moreFightsLoaded = ref(false);
    const loadingMoreFights = ref(false);
    const competitorIdContext = ref<CompetitorIdContext | null>(null);
    const competitorRecordProfile = ref<CompetitorRecordProfile | CompetitorOwnRecordProfile>(emptyProfile());
    const activeTab = ref<"history" | "statistics">("history");
    const statistics = ref<RecordProfileStatistics | null>(null);
    const loadingStatistics = ref(false);

    watch(activeTab, tab => {
        if (tab !== "statistics" || statistics.value !== null || loadingStatistics.value) {
            return;
        }

        let request: Promise<RecordProfileStatistics>;
        if (competitorIdContext.value) {
            const { userId, competitorId } = competitorIdContext.value;
            request = recordProfileApi.getOwnStatistics(userId, competitorId);
        } else if (isPublicProfile(competitorRecordProfile.value)) {
            request = recordProfileApi.getCompetitorStatistics(competitorRecordProfile.value.id);
        } else {
            return;
        }

        loadingStatistics.value = true;
        request
            .then(result => {
                statistics.value = result;
            })
            .catch(handleError)
            .finally(() => {
                loadingStatistics.value = false;
            });
    });

    function resetStatistics(): void {
        activeTab.value = "history";
        statistics.value = null;
        loadingStatistics.value = false;
    }

    const probablyMoreFightsToLoad = computed(() => competitorRecordProfile.value.fightRecords.length >= 5 && !moreFightsLoaded.value);
    const canGoBack = computed(() => historyStack.value.length > 1);

    function emptyProfile(): CompetitorOwnRecordProfile {
        return {
            competitorId: 0,
            firstName: "",
            lastName: "",
            nationality: null,
            academyName: "",
            academyId: 0,
            branch: "",
            overallFightRecord: { wins: 0, losses: 0 },
            fightRecords: [],
            photoUrl: null,
        };
    }

    function resetState(): void {
        loadingMoreFights.value = false;
        requireLogin.value = false;
        requireVerifiedRegistrations.value = false;
        initDone.value = false;
        moreFightsLoaded.value = false;
        competitorRecordProfile.value = emptyProfile();
        historyStack.value = [];
        resetStatistics();
    }

    function open(eventId: number, publicId: string): Promise<void> {
        resetState();
        competitorIdContext.value = null;
        modal.value?.open();

        return reloadCompetitor(eventId, publicId)
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function openByCompetitorId(userId: number, competitorId: number): Promise<void> {
        resetState();
        competitorIdContext.value = { userId, competitorId };
        modal.value?.open();

        return recordProfileApi
            .getOwnRecordProfile(userId, competitorId)
            .then(([profile, statusCode]) => {
                if (statusCode === 403) {
                    requireLogin.value = true;
                    return;
                }
                if (profile) {
                    historyStack.value.push({ type: "own", userId, competitorId });
                    competitorRecordProfile.value = profile;
                }
            })
            .catch(handleError)
            .finally(() => {
                initDone.value = true;
            });
    }

    function reloadCompetitor(eventId: number, publicId: string): Promise<void> {
        moreFightsLoaded.value = false;
        resetStatistics();

        return recordProfileApi.getCompetitorRecordProfile(eventId, publicId).then(([profile, statusCode]) => {
            if (statusCode === 401) {
                requireLogin.value = true;
                return;
            }
            if (statusCode === 403) {
                requireVerifiedRegistrations.value = true;
                return;
            }
            if (profile) {
                competitorIdContext.value = null;
                historyStack.value.push({ type: "public", eventId, publicId });
                competitorRecordProfile.value = profile;
            }
        });
    }

    function loadMoreFights(): Promise<void> {
        loadingMoreFights.value = true;

        if (competitorIdContext.value) {
            const { userId, competitorId } = competitorIdContext.value;
            return recordProfileApi
                .getFullFightRecordsByCompetitorId(userId, competitorId)
                .then((fights: FullFightRecord[]) => {
                    moreFightsLoaded.value = true;
                    competitorRecordProfile.value.fightRecords = fights;
                })
                .catch(handleError)
                .finally(() => {
                    loadingMoreFights.value = false;
                });
        }

        if (!isPublicProfile(competitorRecordProfile.value)) {
            loadingMoreFights.value = false;
            return Promise.resolve();
        }

        return recordProfileApi
            .getFullFightRecords(competitorRecordProfile.value.id)
            .then((fights: FullFightRecord[]) => {
                moreFightsLoaded.value = true;
                competitorRecordProfile.value.fightRecords = fights;
            })
            .catch(handleError)
            .finally(() => {
                loadingMoreFights.value = false;
            });
    }

    function goBack(): void {
        if (!canGoBack.value) {
            return;
        }

        historyStack.value.pop();
        const previousEntry = historyStack.value[historyStack.value.length - 1];
        moreFightsLoaded.value = false;
        resetStatistics();

        if (previousEntry.type === "own") {
            competitorIdContext.value = {
                userId: previousEntry.userId,
                competitorId: previousEntry.competitorId,
            };

            recordProfileApi
                .getOwnRecordProfile(previousEntry.userId, previousEntry.competitorId)
                .then(([profile, statusCode]) => {
                    if (statusCode === 403) {
                        requireLogin.value = true;
                        return;
                    }
                    if (profile) {
                        competitorRecordProfile.value = profile;
                    }
                })
                .catch(handleError);
            return;
        }

        competitorIdContext.value = null;

        recordProfileApi
            .getCompetitorRecordProfile(previousEntry.eventId, previousEntry.publicId)
            .then(([profile, statusCode]) => {
                if (statusCode === 401) {
                    requireLogin.value = true;
                    return;
                }
                if (statusCode === 403) {
                    requireVerifiedRegistrations.value = true;
                    return;
                }
                if (profile) {
                    competitorRecordProfile.value = profile;
                }
            })
            .catch(handleError);
    }

    defineExpose({ open, openByCompetitorId });
</script>

<style scoped>
    .gold {
        color: #ffc501;
    }

    .silver {
        color: #cecece;
    }

    .bronze {
        color: #ffa265;
    }

    .competitor-id {
        font-size: 0.8rem;
    }

    .profile-header {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-around;
        align-items: center;
        gap: 1.5rem;
    }

    .profile-identity {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
        max-width: 100%;
        min-width: 0;
    }

    .profile-details {
        max-width: 100%;
        min-width: 0;
    }

    .profile-academy {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .profile-stats {
        text-align: center;
    }

    @media screen and (max-width: 768px) {
        .profile-stats {
            width: 100%;
        }

        .profile-identity {
            justify-content: center;
            text-align: center;
        }
    }
</style>
